import React, {
  createContext,
  useContext,
  ReactNode,
} from "react";
import { useProjectStore } from "entity-store/project-store/use-project-store";
import { useAuthContext } from "BEDROCK/auth/auth-provider";
import { useProjectFormatter } from "hooks/use-project-formatter";
import { useCheckoutPricing } from "hooks/use-checkout-pricing";
import { useQueryClient } from "@tanstack/react-query";
import { ShippingAddress } from "entity-store/address-store/use-address-store";

export type CheckoutError = "unaunthorized" | "unknown";

type CheckoutState = {
  items: string[];
};

type CheckoutContextType = {
  checkout: CheckoutState;
  addProject: (id: string) => Promise<void>;
  removeItem: (id: string) => Promise<void>;
  clearCheckout: () => void;
  clearAllAndDelete: () => Promise<void>;
  isEmpty: boolean;
  pay: (deliveryAddress: ShippingAddress) => Promise<void>;
};

const CheckoutContext = createContext<CheckoutContextType | undefined>(
  undefined
);

export const CheckoutProvider = ({ children }: { children: ReactNode }) => {
  const { formatForCheckout } = useProjectFormatter();
  const { formattedPriceForProjects } = useCheckoutPricing();
  const {
    loadProject,
    saveProject,
    checkoutProjects,
    deleteProjectAsync,
  } = useProjectStore();
  const { session } = useAuthContext();
  const queryClient = useQueryClient();

  // Derive checkout state from checkoutProjects query
  const checkout: CheckoutState = {
    items: checkoutProjects?.map(p => p.id) ?? [],
  };

  const addProject = async (projectID: string) => {
    const project = loadProject(projectID);
    if (project) {
      const updatedProject = { ...project, isInCheckout: true };
      saveProject(updatedProject, true);
      // Wait a moment for the save to complete, then refetch
      await new Promise(resolve => setTimeout(resolve, 500));
      await queryClient.refetchQueries({ queryKey: ["checkoutProjects"] });
    }
  };

  const removeItem = async (id: string) => {
    // First try to find the project in checkoutProjects (fetched from Supabase)
    // Fall back to loadProject (local storage) for backwards compatibility
    const project = checkoutProjects?.find(p => p.id === id) ?? loadProject(id);
    if (project) {
      await deleteProjectAsync(project);
      // Explicitly refetch after deletion
      await queryClient.refetchQueries({ queryKey: ["checkoutProjects"] });
    }
  };

  const clearCheckout = () => {
    // This function is kept for compatibility (used in sign-out flow)
    // It doesn't actually clear projects, just resets local state
    queryClient.setQueryData(["checkoutProjects"], []);
  };

  const clearAllAndDelete = async () => {
    // Delete all projects in checkout
    const projects = checkoutProjects ?? [];
    await Promise.all(
      projects.map(async (project) => {
        await deleteProjectAsync(project);
      })
    );
    // Explicitly refetch after deletions
    await queryClient.refetchQueries({ queryKey: ["checkoutProjects"] });
  };

  const pay = async (deliveryAddress: ShippingAddress) => {
    try {
      const token = session?.access_token;
      if (!token) {
        throw new Error("User is not authenticated");
      }
      const url = process.env.REACT_APP_CHECKOUT_URL!;

      const items = checkout.items.map((id) => {
        return formatForCheckout(id);
      });
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          items: items,
          //Used in the confirmation email, not by Stripe.
          totalPriceString: formattedPriceForProjects(checkoutProjects ?? []),
          deliveryAddress: {
            line1: deliveryAddress.line1,
            line2: deliveryAddress.line2,
            city: deliveryAddress.city,
            postcode: deliveryAddress.postcode,
            country: deliveryAddress.country,
          },
        }),
      });
      if (!response.ok) {
        if (response.status === 401) {
          const err: CheckoutError = "unaunthorized";
          throw new Error(err);
        }
        throw new Error(`Request failed with status ${response.status}`);
      }
      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      const message: CheckoutError = "unknown";
      throw new Error(message);
    }
  };

  const isEmpty = checkout.items.length === 0;

  return (
    <CheckoutContext.Provider
      value={{
        checkout,
        addProject,
        clearCheckout,
        clearAllAndDelete,
        isEmpty,
        pay,
        removeItem,
      }}
    >
      {children}
    </CheckoutContext.Provider>
  );
};

export const useCheckout = () => {
  const context = useContext(CheckoutContext);
  if (!context) {
    throw new Error("useCheckout must be used within a CheckoutProvider");
  }
  return context;
};
